import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

/**
 * One hovered data point, shared between a chart and the tiles that describe it.
 *
 * ── What this is for ──
 * `ChartSection` puts a column of tiles beside a plot: on Home those read "Total deposited",
 * "Depositors", "Average", "Largest". Two of them name a SPECIFIC point on the line — the total
 * is the last point, the largest deposit is one particular spike — and until this existed there
 * was no way to tell which. The figure said 150,000 and the chart had nine markers.
 *
 * So the link runs both ways. Hovering a tile rings its point on the chart; hovering a point
 * lifts the tile it feeds. Nothing is rewritten and no figure changes meaning under the cursor —
 * the interaction only ever POINTS, which is why it can be driven by a single shared index.
 *
 * ── Why a context and not a prop ──
 * `ChartSection` takes the chart as `children`, so it cannot pass props into it without
 * `cloneElement` — which would silently do nothing the moment someone wraps the chart in a
 * fragment or a memo. A context is read by whoever wants it, at any depth, and a chart that
 * does not care simply never calls the hook.
 */
interface ChartFocus {
  /** Index into the chart's own point array, or null when nothing is hovered. */
  focus: number | null
  setFocus: (i: number | null) => void
}

const Ctx = createContext<ChartFocus | null>(null)

export function ChartFocusProvider({ children }: { children: ReactNode }) {
  const [focus, setFocus] = useState<number | null>(null)
  const value = useMemo(() => ({ focus, setFocus }), [focus])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * The shared focus, falling back to LOCAL state when there is no provider.
 *
 * The fallback is the point of the hook rather than an afterthought: `BondFlowChart` owns a
 * perfectly good hover state of its own and is rendered outside a `ChartSection` in tests and
 * could be again anywhere else. Throwing here — the right call for a provider a component
 * genuinely cannot work without, as in layout/theme.tsx — would make the chart's own tooltip
 * depend on a section it has no other reason to know about.
 *
 * `useState` runs unconditionally so the hook order is stable whether or not a provider exists;
 * the unused half costs one slot and nothing else.
 */
export function useChartFocus(): ChartFocus {
  const ctx = useContext(Ctx)
  const [focus, setFocus] = useState<number | null>(null)
  const local = useMemo(() => ({ focus, setFocus }), [focus])
  return ctx ?? local
}
